import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { useFGTSMutations } from "@/app/hooks/useFGTSMutations";
import { createFGTSTransactionBody } from "@/app/services/selfHireService/createFGTSTransaction";
import { PartnerType } from "@/app/utils/partners.enum";
import { useToast } from "@/view/components/ui/use-toast";
import { parse } from "date-fns";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";
import { CreditPartner } from "../../useCreditController";
import type { createPublicCustomerBody } from "@/app/services/selfHireService/createPublicCustomer";
import { Table } from "@/app/entities/TableCredit";
import { useQuery } from "@tanstack/react-query";
import { transactionsService } from "@/app/services/transactionService";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { cepService } from "@/app/services/cepService";
import { selfHireService } from "@/app/services/selfHireService";
import masks from "@/app/utils/masks";

export const useFinalizationForm = (methods: UseFormReturn<any>) => {
  const { fgtsData, nextStep, type, panData, cltData, crefazData } =
    useSelfHiringContext();
  const { linkName } = useParams();
  const { toast } = useToast();

  const { createCustomerFGTSMutation, createTransactionFGTSMutation } =
    useFGTSMutations();

  const { createCustomerMutation, sendProposalPanMutation } =
    useSelfHiringMutation();

  const [isLoading, setIsLoading] = useState(false);

  const { data: banksPan } = useQuery({
    queryKey: ["banksPan"],
    queryFn: () => transactionsService.banks(),
    enabled: type === PartnerType.PAN,
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }));
    },
  });

  const parseDate = (str: string) =>
    parse(str, "dd/MM/yyyy", new Date()).toISOString();

  const sanitizeNumber = (value: string) => value?.replace(/\D/g, "") || "";

  const buildCustomerPayload = () => {
    const formData = methods.getValues();

    return {
      name: formData.name,
      cpf: sanitizeNumber(formData.cpf),
      birthDate: parseDate(formData.birthDate),
      email: formData.email,
      phone: sanitizeNumber(formData.phone),
      rg: {
        ...formData.rg,
        number: sanitizeNumber(formData.rg.number),
        date: parseDate(formData.rg.date),
      },
      address: {
        ...formData.address,
        zipCode: sanitizeNumber(formData.address.zipCode),
        number: formData.address.number || "",
      },
      bankAccount: formData.bankAccount,
    };
  };

  const buildTransactionPayload = (
    customerId: string,
    table: Table | null,
  ): createFGTSTransactionBody => {
    if (!table) throw new Error("Tabela não encontrada");

    return {
      value: (table.ValorSolicitado ?? 0) * 100,
      customerId,
      CodigoTabela: table.CodigoTabela ?? "",
      CodigoProduto: table.CodigoProduto ?? 0,
      CodigoEmpregador: table.CodigoEmpregador ?? 0,
      CodigoOrg: table.CodigoOrgao ?? 0,
      Prazo: table.Prazo ?? 0,
      linkName: linkName!,
      type: type || "",
    };
  };

  const submitFGTS = async () => {
    try {
      const customerPayload = buildCustomerPayload();
      const customer = await createCustomerFGTSMutation.mutateAsync({
        ...customerPayload,
        leadId: fgtsData?.leadId as string,
      });

      const transactionPayload = buildTransactionPayload(
        customer._id,
        fgtsData?.table as Table | null,
      );
      await createTransactionFGTSMutation.mutateAsync(transactionPayload);
    } catch (error) {
      console.error("Erro FTGTS:", error);
      throw error;
    }
  };

  const submitCrefaz = async () => {
    try {
      const updatedCustomerPayload = buildCustomerPayload();
      const formData = methods.getValues();
      await createCustomerMutation.mutateAsync({
        ...updatedCustomerPayload,
        ocupationId: "1",
        placeOfBirth: {
          city: formData.placeOfBirth?.city ?? "",
          uf: formData.placeOfBirth?.uf ?? "",
        },
      } as createPublicCustomerBody);

      if (crefazData?.transactionId) {
        await transactionsService.editTransaction({
          transactionId: crefazData.transactionId,
          crefazValidation: true,
        });
      }
    } catch (error) {
      console.error("Erro CREFAZ:", error);
      throw error;
    }
  };

  const submitPan = async () => {
    try {
      const customerPayload = buildCustomerPayload();
      await createCustomerMutation.mutateAsync(
        customerPayload as createPublicCustomerBody,
      );

      if (panData?.proposal) {
        await sendProposalPanMutation.mutateAsync({
          id: panData.state.transactionId,
          payload: panData.proposal,
        });
      }
    } catch (error) {
      console.error("Erro PAN:", error);
      throw error;
    }
  };

  const submitClt = async () => {
    try {
      const customerPayload = buildCustomerPayload();
      const customer = await createCustomerMutation.mutateAsync(
        customerPayload as createPublicCustomerBody,
      );

      const selectedTable =
        cltData?.tables?.find((t) => t.Prazo === cltData?.selectedPrazo) ??
        cltData?.tables?.[0];
      if (!selectedTable || !cltData?.matricula || !cltData?.cnpjEmpresa) {
        throw new Error("Dados de CLT incompletos para criar a transação");
      }

      const valorSolicitado = Number(selectedTable.ValorSolicitado ?? 0);

      await selfHireService.createCltTransaction({
        linkName: linkName || "",
        customerId: customer._id,
        value: valorSolicitado,
        tableCode: selectedTable.tableCode,
        Prazo: Number(selectedTable.Prazo),
        codigoTabela: Number(selectedTable.CodigoTabela ?? 0),
        codigoProduto: Number(selectedTable.CodigoProduto ?? 0),
        codigoEmpregador: Number(selectedTable.CodigoEmpregador ?? 0),
        codigoOrgao: Number(selectedTable.CodigoOrgao ?? 0),
        valorParcela: Number(selectedTable.ValorParcela ?? 0),
        taxa: Number(selectedTable.Taxa ?? 0),
        matricula: Number(cltData.matricula),
        cnpjEmpresa: cltData.cnpjEmpresa,
      });
    } catch (error) {
      console.error("Erro CLT:", error);
      throw error;
    }
  };

  const handleSubmit = methods.handleSubmit(async () => {
    try {
      setIsLoading(true);
      await submitMap[type as CreditPartner]();
      nextStep();
    } catch (error) {
      toast({
        title: "Ocorreu um erro, tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  });

  const submitMap = {
    [PartnerType.FGTS]: submitFGTS,
    [PartnerType.CREFAZ]: submitCrefaz,
    [PartnerType.PAN]: submitPan,
    [PartnerType.CLT]: submitClt,
  };

  const handleCepRequest = async (cep: string) => {
    const response = await cepService(cep.replace(/\D/g, ""));

    if (response) {
      methods.setValue("address.city", response.localidade);
      methods.setValue("address.complement", response.complemento);
      methods.setValue("address.neighborhood", response.bairro);
      methods.setValue("address.state", response.uf);
      methods.setValue("address.street", response.logradouro);
    }
  };

  useEffect(() => {
    const values = methods.getValues();

    if (values?.zipCode) {
      methods.setValue("address.zipCode", masks.unmask(values.zipCode));
      handleCepRequest(values.zipCode);
    }
  }, [type, linkName]);

  return {
    handleSubmit,
    isLoading,
    banksPan: banksPan ?? [],
    handleCepRequest,
  };
};
